import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select, Tooltip, Icon, Spin, Empty } from "antd";
import MoveTypeBadge, { contrastText } from "../common/MoveTypeBadge";
import { deferFocus } from "../../helpers/focusUtils";
import { GetCashIcon, ChevronDown } from "../../common/CustomIcon";

import "../../../stylesheets/linchpin/components.css";
import "../../../stylesheets/linchpin/order-create.css";
import "./ServiceLevelHeader.css";

// Phase 4.3 Mode A — the "Service Level" block that sits between the account
// block (AccountBlockV2) and the dynamic legs on CreateOrderScreen. Account
// selection is owned by AccountBlockV2; this block owns ONLY the service-level
// dropdown + its move-type badge. Presentational: no fetching, no order state.

// 2026-08-07: default LOS (Tariff) leg chips hidden in the SL dropdown per
// product request. Helpers kept (commented) so they can be restored with the
// chip block below.
// "SHIPPER_PICKUP" -> "Shipper Pickup" for the option legs summary.
// const humanizeRole = (role) =>
//   String(role || "")
//     .toLowerCase()
//     .replace(/_/g, " ")
//     .replace(/\b\w/g, (c) => c.toUpperCase());

// Legs with a resolved tariff, in sequence — the dropdown renders these as
// chips and silently skips legs with nothing assigned (no "—" noise).
// const legsWithLos = (sl) =>
//   (sl.leg_los || [])
//     .filter((l) => l.effective_los_code)
//     .slice()
//     .sort((a, b) => a.leg_sequence - b.leg_sequence);

// 2026-07-13: tooltip_label dropped from the SL dropdown tooltip (per product) —
// the tooltip shows only the message paragraphs now.
const buildTooltipTitle = (sl) => {
  const messages = Array.isArray(sl.tooltip_message) ? sl.tooltip_message.filter(Boolean) : [];
  if (!messages.length) return null;
  return (
    <div>
      {messages.map((p, i) => <div key={i}>{p}</div>)}
    </div>
  );
};

// The badge carries the move type's own display_color (same as the dropdown
// options' MoveTypeBadge) so the selected value reflects the move type — not a
// flat brand-blue box. Falls back to the CSS default when no color is resolved.
const SlCodeIcon = ({ code, color }) => (
  <span
    className="sl-box__code-icon"
    aria-hidden
    style={color ? { background: color, color: contrastText(color) } : undefined}
  >
    {(code || "").slice(0, 3).toUpperCase()}
  </span>
);

const SlFilledValue = ({ name, subtitle, code, color }) => (
  <div className="sl-box__select-filled">
    <SlCodeIcon code={code} color={color} />
    <span className="sl-box__select-text">
      <span className="sl-box__select-name">{name}</span>
      {subtitle ? <span className="sl-box__select-sub">{subtitle}</span> : null}
    </span>
  </div>
);

const ServiceLevelHeader = (props) => {
  const {
    serviceLevels = [], serviceLevelIndex = {},
    selectedServiceLevelId, loadingServiceLevels = false, onChangeServiceLevel,
    // Details/view screens: render the selected service level as plain text
    // (no dropdown, nothing changeable).
    readOnly = false,
    // Create screen: show the Figma default select before account / Mode A is ready.
    disabled = false,
  } = props;

  // Move-type badge info for a service level. Falls back to the move-type-keyed
  // entry (organization_move_type_id) when there's no per-SL entry — a
  // DEACTIVATED (soft-deleted) SL is absent from the .active-scoped catalog, so
  // its badge would otherwise render blank. Returns {} when nothing resolves.
  const mtInfoFor = (sl) =>
    (sl && (serviceLevelIndex[sl.service_level_id] || serviceLevelIndex[`omt:${sl.organization_move_type_id}`])) || {};

  const selectedSl = serviceLevels.find((s) => s.service_level_id === selectedServiceLevelId) || null;
  const selectedBadge = selectedSl ? mtInfoFor(selectedSl) : null;

  // Account-first flow: once the chosen account's service levels finish loading
  // and nothing is selected yet, move focus onto the SL picker AND open it —
  // with showSearch the type-ahead input only exists while the dropdown is open,
  // so focus alone highlights the box but places no cursor. Falling edge of the
  // loading flag only, so we never steal focus afterwards. `open` is controlled
  // but synced to every normal interaction via onDropdownVisibleChange.
  const selectRef = useRef(null);
  const prevLoadingRef = useRef(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // With CONTROLLED `open`, rc-select never moves focus onto its inner search
  // input on programmatic opens — and that input is where ALL the keyboard
  // handling (arrow navigation, Enter-to-pick, type-ahead) lives. Verified in
  // the browser: arrows are dead on the outer .ant-select-selection and work
  // the moment the inner input has focus. So every programmatic open chases
  // focus onto it (rc-select instance API first, DOM query as fallback).
  const focusSlSearchInput = () =>
    deferFocus(
      () =>
        selectRef.current?.rcSelect?.getInputDOMNode?.() ||
        document.querySelector('[data-testid="mode-a-sl-row"] .ant-select-search__field'),
      100
    );
  useEffect(() => {
    if (disabled || readOnly) return;
    if (!readOnly && prevLoadingRef.current && !loadingServiceLevels && serviceLevels.length > 0 && !selectedServiceLevelId) {
      selectRef.current?.focus?.();
      setDropdownOpen(true);
      focusSlSearchInput();
    }
    prevLoadingRef.current = loadingServiceLevels;
  }, [loadingServiceLevels, serviceLevels.length, selectedServiceLevelId, readOnly, disabled]);

  const filledSubtitle = useMemo(() => {
    const alias = selectedBadge?.alias_name;
    if (!alias || alias === selectedSl?.name) return null;
    return alias;
  }, [selectedBadge, selectedSl]);

  const slSelectSuffix = <ChevronDown />;

  const renderSelectControl = () => {
    if (disabled) {
      return (
        <Select
          className="sl-box__select sl-box__select--disabled"
          placeholder="Select Service Level"
          disabled
          value={undefined}
          suffixIcon={slSelectSuffix}
          data-testid="mode-a-sl-select"
        />
      );
    }

    if (readOnly) {
      if (loadingServiceLevels) return <Spin />;
      return (
        <span
          data-testid="mode-a-sl-readonly"
          className={["sl-box__readonly", !selectedSl ? "sl-box__readonly--empty" : "sl-box__readonly--filled"].filter(Boolean).join(" ")}
        >
          {selectedSl ? (
            <SlFilledValue
              name={selectedSl.name}
              subtitle={filledSubtitle}
              code={selectedBadge?.short_code}
              color={selectedBadge?.display_color}
            />
          ) : (
            "Select Service Level"
          )}
        </span>
      );
    }

    if (loadingServiceLevels) return <Spin />;
    if (serviceLevels.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No service levels available for this account"
          style={{ margin: 0 }}
        />
      );
    }

    return (
      // Keyboard: `open` is CONTROLLED (for the account->SL auto-open),
      // which can swallow the native Enter/ArrowDown open-when-focused.
      // This capture wrapper restores it; Space is left alone (typing).
      <span
        className="sl-box__select-wrap"
        onKeyDown={(e) => {
          if (!dropdownOpen && ["Enter", "ArrowDown"].includes(e.key)) {
            e.preventDefault();
            setDropdownOpen(true);
            focusSlSearchInput();
          }
        }}
      >
        {selectedSl && !dropdownOpen && (
          <SlFilledValue
            name={selectedSl.name}
            subtitle={filledSubtitle}
            code={selectedBadge?.short_code}
            color={selectedBadge?.display_color}
          />
        )}
        <Select
          ref={selectRef}
          className={["sl-box__select", selectedSl ? "sl-box__select--filled" : ""].filter(Boolean).join(" ")}
          placeholder="Select Service Level"
          value={selectedSl ? selectedServiceLevelId : undefined}
          onChange={onChangeServiceLevel}
          optionLabelProp="label"
          dropdownMatchSelectWidth={false}
          dropdownStyle={{ minWidth: 440, maxWidth: 560 }}
          dropdownClassName="mode-a-sl-dropdown"
          dropdownAlign={{ points: ["tr", "br"], overflow: { adjustX: true, adjustY: true } }}
          data-testid="mode-a-sl-select"
          open={dropdownOpen}
          onDropdownVisibleChange={(v) => setDropdownOpen(v)}
          showSearch={dropdownOpen}
          suffixIcon={slSelectSuffix}
          filterOption={(input, option) => {
            const sl = serviceLevels.find((s) => s.service_level_id === option.props.value);
            if (!sl) return false;
            const mt = mtInfoFor(sl);
            const q = String(input).toLowerCase();
            return [sl.name, sl.code, mt.alias_name, mt.short_code]
              .some((s) => s && String(s).toLowerCase().includes(q));
          }}
        >
          {serviceLevels.map((sl) => {
            const mt = mtInfoFor(sl);
            const tip = buildTooltipTitle(sl);
            // A deactivated (soft-deleted) SL is only present here because it is the
            // order's OWN saved SL (retained via include_service_level_id on edit).
            // Show it selected but disabled + labelled so it can't be re-picked for
            // a new order, and the user sees what the order was created with.
            const isDeactivated = sl.is_active === false;
            // 2026-08-07: default LOS (Tariff) leg chips hidden per product request.
            // const legs = legsWithLos(sl);
            return (
              <Select.Option
                key={sl.service_level_id}
                value={sl.service_level_id}
                disabled={isDeactivated}
                label={isDeactivated ? `${sl.name} (deactivated)` : sl.name}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <span style={{ flexShrink: 0, marginTop: 1 }}>
                    <MoveTypeBadge shortCode={mt.short_code} color={mt.display_color} title={mt.alias_name} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: 600, color: "#273156", lineHeight: "20px" }}>{sl.name}</span>
                      {isDeactivated ? (
                        <span style={{ fontSize: 11, lineHeight: "16px", color: "#B42318", background: "#FEF3F2", border: "1px solid #FECDCA", borderRadius: 9999, padding: "0 8px", flexShrink: 0 }}>
                          deactivated
                        </span>
                      ) : null}
                      {tip ? (
                        <Tooltip title={tip} placement="right">
                          <Icon type="info-circle" style={{ color: "#98A2B3", fontSize: 13, marginLeft: "auto", flexShrink: 0 }} onClick={(e) => e.stopPropagation()} />
                        </Tooltip>
                      ) : null}
                    </div>
                    {sl.description ? (
                      <div style={{ fontSize: 12, color: "#667085", whiteSpace: "normal", lineHeight: "17px", marginTop: 1 }}>
                        {sl.description}
                      </div>
                    ) : null}
                    {/* 2026-08-07: default LOS (Tariff) leg chips hidden in the SL dropdown per product request.
                    {legs.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 5 }}>
                        {legs.map((l) => (
                          <span
                            key={l.leg_sequence}
                            style={{
                              fontSize: 11,
                              lineHeight: "16px",
                              color: "#475467",
                              background: "#F2F4F7",
                              border: "1px solid #E4E7EC",
                              borderRadius: 9999,
                              padding: "0 8px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {humanizeRole(l.location_role)} · {l.effective_los_code}
                          </span>
                        ))}
                      </div>
                    )}
                    */}
                  </div>
                </div>
              </Select.Option>
            );
          })}
        </Select>
      </span>
    );
  };

  return (
    <div className="lp-v3">
      <div className="sl-box" data-testid="mode-a-sl-row">
        <div className="sl-box__left">
          <span className="sl-box__icon">
            <GetCashIcon color="#586FCC" width="24" height="24" />
          </span>
          <span className="sl-box__title">Service Level</span>
        </div>
        <div className="sl-box__right">
          {renderSelectControl()}
        </div>
      </div>
    </div>
  );
};

export default ServiceLevelHeader;
